/* eslint-disable formatjs/no-literal-string-in-jsx */
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import Link from '@atlaskit/link';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';

import ChromeIcon from '../../../../../assets/chrome-icon.svg';
import RovoIcon from '../../../../../assets/rovo-icon.svg';
import { useIntl } from 'react-intl-next';

import { type Source } from '../types';
import { COPY_VARIATIONS } from '../messages';

const styles = cssMap({
	boxContainer: {
		display: 'flex',
		width: '100%',
		padding: token('space.150', '12px'),
		alignItems: 'flex-start',
		gap: token('space.100', '8px'),
		borderRadius: token('radius.small', '4px'),
		backgroundColor: token('color.background.accent.blue.subtlest'),
		placeContent: 'center',
	},
	mainContent: {
		display: 'flex',
		gap: token('space.100', '8px'),
		alignItems: 'flex-start',
		width: '100%',
	},
	rightColumn: {
		gap: token('space.100', '8px'),
		flex: 1,
	},
	headerInline: {
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	iconContainer: {
		display: 'flex',
		alignItems: 'center',
	},
});

const getLinkUrl = (source: Source, cohort: string) => {
	const UTM_MEDIUM = 'banner';
	const UTM_CAMPAIGN = 'rovo_extension_side_nav_install_cta';
	const UTM_CONTENT = `side_nav_install_cta_${cohort}`;
	return `https://chromewebstore.google.com/detail/rovo-search-chat-and-more/npbjhobkibekbklkghlhfhieggcggpdb?utm_source=${source}&utm_medium=${UTM_MEDIUM}&utm_campaign=${UTM_CAMPAIGN}&utm_content=${UTM_CONTENT}`;
};

const selectCopyForCohort = (cohort: string) => {
	return (
		COPY_VARIATIONS[cohort as keyof typeof COPY_VARIATIONS] || COPY_VARIATIONS['copy-version-a']
	);
};

type LeftNavCtaMessageProps = {
	handleOnDismissed: () => void;
	context: {
		experimentCohort: string;
		source: Source;
		[key: string]: unknown;
	};
};

export const LeftNavCtaMessage = ({ handleOnDismissed, context }: LeftNavCtaMessageProps) => {
	const { formatMessage } = useIntl();
	const { experimentCohort, source } = context;
	const { heading, description } = selectCopyForCohort(experimentCohort);

	const { messageClicked } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const headingText = formatMessage(heading);
	const descriptionText = formatMessage(description);
	const linkText = formatMessage({
		id: 'rovo-extension-install-cta-left-nav.link',
		defaultMessage: 'Get the browser extension',
		description: 'Text for rovo extension install cta link left nav',
	});

	const linkUrl = getLinkUrl(source, experimentCohort);

	const handleDismiss = () => {
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source,
			},
		});
		handleOnDismissed();
	};

	const handleLinkClick = () => {
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'installLink',
				clickedElement: 'link',
			},
			action: {
				actionType: 'open',
				destinationUrl: linkUrl,
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source,
			},
		});
	};

	return (
		<Box xcss={cx(styles.boxContainer)} style={{ border: `1px solid ${token('color.border')}` }}>
			<Box xcss={cx(styles.mainContent)}>
				<Box xcss={cx(styles.iconContainer)}>
					<Image
						src={RovoIcon}
						alt="Rovo Icon"
						width={24}
						height={24}
						style={{ flexShrink: 0, zIndex: 2 }}
					/>
					<Image
						src={ChromeIcon}
						alt="Google Chrome Icon"
						width={24}
						height={24}
						style={{ marginLeft: '-8px', flexShrink: 0, zIndex: 1 }}
					/>
				</Box>

				<Stack xcss={cx(styles.rightColumn)} testId="rovo-left-nav-right-column">
					<Inline alignBlock="center" xcss={cx(styles.headerInline)}>
						<Heading size="xsmall">{headingText}</Heading>
						<IconButton
							icon={CrossIcon}
							label="Dismiss message"
							appearance="subtle"
							onClick={handleDismiss}
						/>
					</Inline>
					<Text size="small">{descriptionText}</Text>
					<Link href={linkUrl} target="blank" onClick={handleLinkClick}>
						{linkText}
					</Link>
				</Stack>
			</Box>
		</Box>
	);
};
